<template>
  <main class="auth-page">
    <div class="auth-split-layout">
      <!-- Image Side (Left) -->
      <div class="auth-image-side d-none d-lg-flex">
        <div class="auth-image-overlay">
          <div class="auth-branding p-64">
            <h2 class="display-2 fw-bold mb-24 line-height-tight letter-spacing-tight branding-title">
              {{ $t('auth.login.brandingTitle') }}
            </h2>
            <p class="lead mb-0 fw-normal max-w-500 branding-subtitle">
              {{ $t('auth.login.brandingSubtitle') }}
            </p>
          </div>

          <div class="branding-bottom p-64 w-100 mt-auto">
            <p class="text-white-50 x-small mb-0 fw-medium">
              © {{ new Date().getFullYear() }} WAW TRAVEL ADVENTURES SL. ALL RIGHTS RESERVED.
            </p>
          </div>
        </div>
      </div>

      <!-- Form Side (Right) -->
      <div class="auth-form-side">
        <div class="auth-form-content">
          <div id="login-logo" class="logo mb-40 text-center">
            <router-link :to="{ name: 'login' }">
              <img
                alt="WAW Travel Logo"
                src="https://site.waw.travel/images/wawlogo.svg"
                class="img-fluid main-logo mx-auto"
              />
            </router-link>
          </div>
          
          <slot/>
        </div>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'VAuthLayout',
});
</script>

<style lang="scss" scoped>
.auth-page {
  height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  color: #2d2d2d;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  font-family: 'Montserrat', Arial, sans-serif;

  @media (max-width: 991px) {
    height: auto;
    overflow-y: auto;
    position: relative;
  }
}

.auth-split-layout {
  display: flex;
  height: 100vh;
  width: 100%;

  @media (max-width: 991px) {
    height: auto;
    min-height: 100vh;
  }
}

.auth-image-side {
  flex: 1.5;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -6%;
    background-image: url('../../../resources/img/DSCF4529-1920x900.jpg');
    background-size: cover;
    background-position: center;
    animation: kenBurns 18s ease-in-out infinite alternate;
    will-change: transform;
    z-index: 0;
  }

  .auth-image-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.8) 100%
    );
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .auth-branding {
    margin-top: 42%;
    margin-bottom: 0;
    padding-bottom: 64px;
    position: relative;
    z-index: 2;

    .branding-title {
      font-family: 'Lama Rounded', 'Montserrat', Arial, sans-serif !important;
      font-weight: 900 !important;
      letter-spacing: -0.03em;
      background: linear-gradient(
        120deg,
        #ff6b9d 0%,
        #e61275 25%,
        #ffd54a 50%,
        #26d0a1 75%,
        #ff6b9d 100%
      );
      background-size: 200% auto;
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      animation:
        brandFadeUp 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both,
        brandingTitleShimmer 4s linear infinite;
    }

    .branding-subtitle {
      font-family: 'Manrope', Arial, sans-serif !important;
      font-weight: 400 !important;
      font-size: 1.15rem;
      line-height: 1.55;
      color: rgba(255, 255, 255, 0.75) !important;
      animation: brandFadeUp 0.85s cubic-bezier(0.16, 1, 0.3, 1) 0.22s both;
    }

    .line-height-tight {
      line-height: 0.95;
    }

    .letter-spacing-tight {
      letter-spacing: -0.03em;
    }

    .max-w-500 {
      max-width: 500px;
    }
  }

  .branding-bottom {
    position: relative;
    z-index: 2;
    animation: brandFadeUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.34s both;

    p {
      font-family: 'Manrope', Arial, sans-serif !important;
      letter-spacing: 0.06em;
    }
  }
}

.auth-form-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  background-color: #ffffff;
  z-index: 1;

  @media (max-width: 991px) {
    flex: 1;
    padding: 40px 24px;
    background-image: linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)),
      url('../../../resources/img/DSCF4529-1920x900.jpg');
    background-size: cover;
    background-position: center;
  }
}

.auth-form-content {
  width: 100%;
  max-width: 400px;
  animation: slideIn 0.85s cubic-bezier(0.16, 1, 0.3, 1);

  .main-logo {
    height: 64px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
    transition: transform 0.35s ease;

    &:hover {
      transform: scale(1.03);
    }
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(28px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes brandFadeUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes kenBurns {
  from {
    transform: scale(1) translate(0, 0);
  }
  to {
    transform: scale(1.12) translate(-1.5%, -1%);
  }
}

@keyframes brandingTitleShimmer {
  0% {
    background-position: 0% center;
  }
  100% {
    background-position: 200% center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-image-side::before {
    animation: none;
  }

  .auth-branding .branding-title,
  .auth-branding .branding-subtitle,
  .branding-bottom,
  .auth-form-content {
    animation: none;
  }

  .auth-branding .branding-title {
    background: none;
    -webkit-text-fill-color: #ffffff;
    color: #ffffff;
  }

  .auth-form-content .main-logo:hover {
    transform: none;
  }
}

.x-small {
  font-size: 0.7rem;
}
</style>
